import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { AlertCircle, Search, Trash2, ChevronLeft, ChevronRight, Loader2, Clock, User, FileText, Server, Eye } from 'lucide-react';
import api from '@/lib/api';

export default function FailedRequests() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [todayCount, setTodayCount] = useState(0);
  const [svcBreakdown, setSvcBreakdown] = useState({});
  const [page, setPage] = useState(1);
  const [service, setService] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: 30 };
      if (service && service !== 'all') params.service = service;
      if (search) params.search = search;
      const res = await api.get('/admin/failed-requests', { params });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 0);
      setTodayCount(res.data.today_count || 0);
      setSvcBreakdown(res.data.service_breakdown || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load');
    }
    setLoading(false);
  }, [page, service, search]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = () => { setSearch(searchInput); setPage(1); };

  const handleDelete = async (refId) => {
    if (!window.confirm(`Delete failed request ${refId}?`)) return;
    try {
      await api.delete(`/admin/failed-requests/${refId}`);
      toast.success('Deleted');
      load();
    } catch (err) { toast.error('Delete failed'); }
  };

  const viewDetail = async (refId) => {
    try {
      const res = await api.get(`/admin/failed-requests/${refId}`);
      setDetail(res.data);
      setShowDetail(true);
    } catch { toast.error('Failed to load details'); }
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <div data-testid="failed-requests-page" className="space-y-4">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">Failed Requests</h2>
        <p className="text-muted-foreground mt-1 text-sm">Track and investigate requests that failed on the GPU server. Each has a reference ID for customer follow-up.</p>
      </div>

      {/* Stats */}
      <div className="flex gap-3 flex-wrap">
        <Badge variant="outline" className={`text-xs py-1.5 px-3 ${todayCount > 0 ? 'border-red-500/40 text-red-400' : ''}`}>
          <AlertCircle className="w-3 h-3 mr-1.5" />{todayCount} failed today
        </Badge>
        <Badge variant="outline" className="text-xs py-1.5 px-3">{total} total</Badge>
        {Object.entries(svcBreakdown).map(([svc, count]) => (
          <Badge key={svc} variant="outline" className="text-xs py-1.5 px-3">{svc}: {count}</Badge>
        ))}
      </div>

      {/* Filters */}
      <Card className="bg-card border-border/50">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <Select value={service} onValueChange={v => { setService(v); setPage(1); }}>
              <SelectTrigger className="w-40 h-8 text-xs"><SelectValue placeholder="All services" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Services</SelectItem>
                <SelectItem value="docling">Docling</SelectItem>
                <SelectItem value="pdf_parser">PDF Parser</SelectItem>
                <SelectItem value="qdrant">Qdrant</SelectItem>
                <SelectItem value="elasticsearch">Elasticsearch</SelectItem>
                <SelectItem value="neo4j">Neo4j</SelectItem>
                <SelectItem value="redis">Redis</SelectItem>
                <SelectItem value="kafka">Kafka</SelectItem>
                <SelectItem value="minio">MinIO</SelectItem>
                <SelectItem value="vane_search">AI Search</SelectItem>
                <SelectItem value="searxng">Web Search</SelectItem>
                <SelectItem value="lightpanda_browser">Browser</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex gap-1 flex-1 min-w-48">
              <Input className="h-8 text-xs" placeholder="Search by email, ref ID, filename, error..." value={searchInput}
                onChange={e => setSearchInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()} />
              <Button size="sm" variant="outline" className="h-8 px-2" onClick={handleSearch}><Search className="w-3 h-3" /></Button>
            </div>
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={load} disabled={loading}>
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Refresh'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      <div className="space-y-2">
        {items.length === 0 ? (
          <Card className="bg-card border-border/50">
            <CardContent className="p-8 text-center">
              <p className="text-sm text-muted-foreground">{loading ? 'Loading...' : 'No failed requests found.'}</p>
            </CardContent>
          </Card>
        ) : items.map(item => (
          <Card key={item.reference_id} className="bg-card border-border/50 hover:border-destructive/20 transition-colors">
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 space-y-1.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className="bg-red-500/20 text-red-400 text-[10px] font-mono">{item.reference_id}</Badge>
                    <Badge variant="outline" className="text-[10px]">{item.service}</Badge>
                    <span className="text-[10px] text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" />{timeAgo(item.created_at)}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><User className="w-3 h-3" />{item.user_email}</span>
                    {item.file_name && <span className="flex items-center gap-1"><FileText className="w-3 h-3" />{item.file_name}</span>}
                    <span className="flex items-center gap-1"><Server className="w-3 h-3" />{item.endpoint}</span>
                  </div>
                  <p className="text-xs text-destructive/80 line-clamp-2">{item.error_detail}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => viewDetail(item.reference_id)}>
                    <Eye className="w-3 h-3 mr-1" />View
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-destructive" onClick={() => handleDelete(item.reference_id)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Page {page} of {totalPages} ({total} total)</span>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft className="w-3 h-3 mr-1" />Previous
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
              Next<ChevronRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-destructive" />
              Failed Request Details
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><span className="text-muted-foreground">Reference ID:</span><p className="font-mono font-bold text-destructive">{detail.reference_id}</p></div>
                <div><span className="text-muted-foreground">Time:</span><p>{detail.created_at}</p></div>
                <div><span className="text-muted-foreground">User:</span><p>{detail.user_email}</p></div>
                <div><span className="text-muted-foreground">Service:</span><p>{detail.service}</p></div>
                <div><span className="text-muted-foreground">Endpoint:</span><p className="font-mono">{detail.endpoint}</p></div>
                <div><span className="text-muted-foreground">File:</span><p>{detail.file_name || 'N/A'}</p></div>
                <div><span className="text-muted-foreground">HTTP Status:</span><p>{detail.error_status}</p></div>
              </div>

              <div>
                <p className="text-xs text-muted-foreground mb-1">Error Detail</p>
                <pre className="bg-background border border-border/50 rounded-lg p-3 text-xs font-mono overflow-auto max-h-40 text-destructive/80">
                  {detail.error_detail}
                </pre>
              </div>

              <div>
                <p className="text-xs text-muted-foreground mb-1">Input Payload</p>
                <pre className="bg-background border border-border/50 rounded-lg p-3 text-xs font-mono overflow-auto max-h-40">
                  {JSON.stringify(detail.input_payload, null, 2)}
                </pre>
              </div>

              {detail.input_payload?.url && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">File URL</p>
                  <a href={detail.input_payload.url} target="_blank" rel="noreferrer" className="text-xs text-primary underline break-all">{detail.input_payload.url}</a>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
